import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import './ThemeSwitch.css';
import ThemeTransitionLoader from './ThemeTransitionLoader';

const ThemeSwitch = ({ 
  className = '', 
  size = 'md',
  showLabels = false,
  position = 'relative',
  onChange 
}) => {
  const [theme, setTheme] = useState('auto');
  const [systemTheme, setSystemTheme] = useState('light');
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Apply theme to document
  const applyTheme = useCallback((selectedTheme, currentSystemTheme = systemTheme) => {
    const root = document.documentElement;
    const body = document.body;
    
    // Add theme-changing class for performance optimization
    body.classList.add('theme-changing');
    
    // Temporarily disable transitions for performance
    root.style.setProperty('--transition-fast', '0ms');
    root.style.setProperty('--transition-normal', '0ms');
    root.style.setProperty('--transition-slow', '0ms');
    
    // Remove existing theme attributes
    root.removeAttribute('data-theme');
    
    if (selectedTheme === 'auto') {
      // Use system preference
      if (currentSystemTheme === 'dark') {
        root.setAttribute('data-theme', 'dark');
      }
      // Light auto: no attribute – :root defaults apply
    } else if (selectedTheme === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else if (selectedTheme === 'light') {
      // Explicit light must set attribute so prefers-color-scheme: dark
      // does not override with dark CSS variables
      root.setAttribute('data-theme', 'light');
    }
    
    // Re-enable transitions and remove theme-changing class after a brief delay
    setTimeout(() => {
      root.style.removeProperty('--transition-fast');
      root.style.removeProperty('--transition-normal');
      root.style.removeProperty('--transition-slow');
      body.classList.remove('theme-changing');
    }, 50);
    
    // Debug: Log the current theme state
    // console.log('Theme applied:', {
    //   selectedTheme,
    //   currentSystemTheme,
    //   dataTheme: root.getAttribute('data-theme'),
    //   effectiveTheme: selectedTheme === 'auto' ? currentSystemTheme : selectedTheme
    // });
  }, [systemTheme]);

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'auto';
    const systemPreference = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    
    setTheme(savedTheme);
    setSystemTheme(systemPreference);
    applyTheme(savedTheme, systemPreference);
  }, [applyTheme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e) => {
      const newSystemTheme = e.matches ? 'dark' : 'light';
      setSystemTheme(newSystemTheme);
      
      // If current theme is auto, update the applied theme
      if (theme === 'auto') {
        applyTheme('auto', newSystemTheme);
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [theme, applyTheme]);

  // Handle theme change with performance optimization
  const handleThemeChange = (newTheme) => {
    setIsTransitioning(true);
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    // Performance optimization: Hide large lists during theme transition
    const largeLists = document.querySelectorAll('.clients-table, .opportunities-table, .leads-table, .property-table, .data-table');
    const originalDisplay = [];
    
    // Hide large tables temporarily
    largeLists.forEach((table, index) => {
      originalDisplay[index] = table.style.display;
      table.style.display = 'none';
    });
    
    // Apply theme
    applyTheme(newTheme);
    
    // Show tables after theme is applied
    setTimeout(() => {
      largeLists.forEach((table, index) => {
        table.style.display = originalDisplay[index] || '';
      });
      setIsTransitioning(false);
    }, 150);
    
    // Add visual feedback with faster transition
    document.body.style.transition = 'background-color 0.15s ease, color 0.15s ease';
    
    // Call external onChange handler
    if (onChange) {
      onChange(newTheme);
    }
  };

  // Get current effective theme for display
  const getEffectiveTheme = useCallback(() => {
    if (theme === 'auto') {
      return systemTheme;
    }
    return theme;
  }, [theme, systemTheme]);

  // Add theme indicator to body for debugging
  useEffect(() => {
    const effectiveTheme = getEffectiveTheme();
    document.body.setAttribute('data-current-theme', effectiveTheme);
  }, [theme, systemTheme, getEffectiveTheme]);

  // Theme options
  const themes = [
    {
      value: 'light',
      label: 'Light',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="5"/>
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
        </svg>
      )
    },
    {
      value: 'dark',
      label: 'Dark',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
      )
    },
    {
      value: 'auto',
      label: 'Auto',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect width="20" height="14" x="2" y="3" rx="2"/>
          <line x1="8" y1="21" x2="16" y2="21"/>
          <line x1="12" y1="17" x2="12" y2="21"/>
        </svg>
      )
    }
  ];

  return (
    <div className={`theme-switch theme-switch-${size} theme-switch-${position} ${className}`}>
      {showLabels && (
        <span className="theme-switch-label">
          Theme: {getEffectiveTheme() === 'dark' ? '🌙' : '☀️'} {themes.find(t => t.value === theme)?.label}
        </span>
      )}
      
      {/* Temporary: Force Light Theme Button for Testing */}
      {/* {process.env.NODE_ENV === 'development' && (
        <button
          onClick={() => {
            console.log('🔧 Force Light Theme Clicked');
            handleThemeChange('light');
          }}
          style={{
            background: '#007bff',
            color: 'white',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            marginRight: '8px',
            cursor: 'pointer'
          }}
        >
          Force Light
        </button>
      )} */}
      
      <div className="theme-switch-buttons" role="radiogroup" aria-label="Choose theme">
        {themes.map((themeOption) => (
          <button
            key={themeOption.value}
            type="button"
            className={`theme-switch-button ${theme === themeOption.value ? 'active' : ''}`}
            onClick={() => handleThemeChange(themeOption.value)}
            aria-label={`Switch to ${themeOption.label.toLowerCase()} theme`}
            aria-pressed={theme === themeOption.value}
            role="radio"
            aria-checked={theme === themeOption.value}
            title={`${themeOption.label} theme${themeOption.value === 'auto' ? ' (follows system)' : ''}`}
          >
            {themeOption.icon}
            {showLabels && (
              <span className="theme-switch-button-label">
                {themeOption.label}
              </span>
            )}
          </button>
        ))}
      </div>
      
      {theme === 'auto' && (
        <div className="theme-switch-indicator">
          <span className="theme-switch-system-indicator">
            System: {systemTheme === 'dark' ? '🌙' : '☀️'}
          </span>
        </div>
      )}
      
      {/* Theme transition loader */}
      <ThemeTransitionLoader 
        isVisible={isTransitioning}
        message="Switching theme..."
      />
    </div>
  );
};

ThemeSwitch.propTypes = {
  className: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  showLabels: PropTypes.bool,
  position: PropTypes.oneOf(['relative', 'fixed-top-right', 'fixed-bottom-right']),
  onChange: PropTypes.func,
};

export default ThemeSwitch;